/**
 * `/winners/lookup/:winId?exp&sig` — opens the claim from a pasted win ID or signed link (§12.3 "Win-ID lookup":
 * mismatched or expired links are reported, never guessed). Assignment, follow-up notes, reminders, "Messaged" /
 * "Followed up" (admin action only, Appendix A) and recording a different handle (Appendix A edge case). Contact
 * data stays masked until unmasked with a purpose (§16.3).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  SocialPlatform,
  formatWinId,
  isValidWinId,
  normalizeWinId,
  type UpdateWinnerContactRequest,
} from '@windrop/contracts';
import { Button, Field, Input, Select, Skeleton, useIntl, useToast } from '@windrop/ui';
import { useState, type FormEvent } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { Can } from '../../auth/guards';
import { useSession } from '../../auth/session';
import { EmptyState, ErrorState } from '../../components/EmptyState';
import { MaskedCell } from '../../components/MaskedCell';
import { PageHeader } from '../../components/PageHeader';
import { StatusBadge, ToneBadge, humanizeStatus } from '../../components/StatusBadge';
import { Timeline } from '../../components/Timeline';
import { winnersBoardApi } from '../../lib/api/admin';
import { isApiError } from '../../lib/api/client';

const LINK_STATE_COPY = {
  none: { tone: 'neutral', text: 'No signed link pasted — opened by win ID.' },
  verified: { tone: 'success', text: 'Signed link verified: this message came from a genuine win link.' },
  expired: {
    tone: 'warning',
    text: 'The signed link has expired. The win is shown by ID; ask the winner for a fresh message if you need the link.',
  },
  mismatch: {
    tone: 'error',
    text: 'The signature does not match this win ID. Do not treat the message as genuine; verify by win ID and the claim details.',
  },
} as const;

export function WinnerDetailPage() {
  const { winId: raw = '' } = useParams();
  const [params] = useSearchParams();
  const intl = useIntl();
  const { toast } = useToast();
  const { session } = useSession();
  const queryClient = useQueryClient();
  const canonical = normalizeWinId(raw);
  const valid = canonical !== null && isValidWinId(canonical);
  const exp = params.get('exp');
  const sig = params.get('sig');
  const link = exp && sig ? { exp: Number(exp), sig } : {};

  const query = useQuery({
    queryKey: ['winner-lookup', canonical, exp, sig],
    queryFn: () => winnersBoardApi.lookup(canonical ?? '', link),
    enabled: valid,
    retry: false,
  });

  const [note, setNote] = useState('');
  const [reminderAt, setReminderAt] = useState('');
  const [outcome, setOutcome] = useState('');
  const [handle, setHandle] = useState('');
  const [handlePlatform, setHandlePlatform] = useState<SocialPlatform>('WHATSAPP');

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['winner-lookup'] });
    void queryClient.invalidateQueries({ queryKey: ['winners'] });
  };
  const update = useMutation({
    mutationFn: (body: UpdateWinnerContactRequest) => winnersBoardApi.updateContact(canonical ?? '', body),
    onSuccess: (row, body) => {
      toast({
        title: body.contactStatus
          ? `Marked ${humanizeStatus(body.contactStatus).toLowerCase()}`
          : 'Winner updated',
        description: row.winIdDisplay,
        tone: 'success',
      });
      setNote('');
      setOutcome('');
      refresh();
    },
    onError: (e) =>
      toast({
        title: 'Update failed',
        description: e instanceof Error ? e.message : undefined,
        tone: 'neutral',
      }),
  });
  const recordHandle = useMutation({
    mutationFn: () =>
      winnersBoardApi.recordHandle(canonical ?? '', {
        platform: handlePlatform,
        handle,
        ...(note ? { note } : {}),
      }),
    onSuccess: () => {
      toast({ title: 'Handle recorded on the profile', tone: 'success' });
      setHandle('');
      setNote('');
      refresh();
    },
    onError: (e) =>
      toast({
        title: 'Could not record the handle',
        description: e instanceof Error ? e.message : undefined,
        tone: 'neutral',
      }),
  });

  if (!valid) {
    return (
      <>
        <PageHeader
          breadcrumb={[{ label: 'Winners board', to: '/winners' }, { label: 'Lookup' }]}
          title="Win ID lookup"
        />
        <ErrorState
          title="That is not a valid win ID"
          body={`“${raw}” fails the check character. Ask the winner to copy the ID exactly as shown on their win screen.`}
          action={<Button href="/winners">Back to winners board</Button>}
        />
      </>
    );
  }
  const display = formatWinId(canonical);
  const data = query.data;
  const w = data?.winner;
  const linkState = data?.linkState ?? (exp || sig ? 'mismatch' : 'none');
  const canMark = (s: 'MESSAGED' | 'FOLLOWED_UP') =>
    w
      ? s === 'MESSAGED'
        ? w.contactStatus === 'NOT_MESSAGED' || w.contactStatus === 'BUTTON_TAPPED'
        : w.contactStatus === 'MESSAGED'
      : false;

  const submitNote = (e: FormEvent) => {
    e.preventDefault();
    const body: UpdateWinnerContactRequest = {};
    if (note.trim()) body.note = note.trim();
    if (reminderAt) body.reminderAt = new Date(reminderAt).toISOString();
    if (Object.keys(body).length) update.mutate(body);
  };

  return (
    <>
      <PageHeader
        breadcrumb={[{ label: 'Winners board', to: '/winners' }, { label: display }]}
        title={display}
        description="Everything the console knows about this win. Contact details stay masked until unmasked with a purpose."
      />
      {query.isPending ? <Skeleton shape="block" height={200} label="Looking up win" /> : null}
      {query.isError ? (
        isApiError(query.error) && query.error.status === 404 ? (
          <EmptyState
            title="No win with this ID in your tenant"
            body="The ID is well-formed but unknown here. The console never guesses."
            action={<Button href="/winners">Back to winners board</Button>}
          />
        ) : (
          <ErrorState
            title="Lookup failed"
            body="Retry in a moment."
            reference={isApiError(query.error) ? query.error.supportReference : undefined}
            action={<Button onClick={() => query.refetch()}>Retry</Button>}
          />
        )
      ) : null}
      {data && w ? (
        <div className="wdc-grid" data-cols="2">
          <p role="status" data-testid="link-state" data-state={linkState} style={{ gridColumn: '1 / -1' }}>
            <ToneBadge tone={LINK_STATE_COPY[linkState].tone}>{humanizeStatus(linkState)}</ToneBadge>{' '}
            {LINK_STATE_COPY[linkState].text}
          </p>
          <section className="wdc-panel">
            <h2>Win and claim</h2>
            <dl className="wdc-facts">
              <div>
                <dt>Lottery</dt>
                <dd>{w.lotteryTitle}</dd>
              </div>
              <div>
                <dt>Prize</dt>
                <dd>
                  {w.prize.name} · {intl.formatMoney(w.prize.value.amountMinor, w.prize.value.currency)}
                  {w.prize.sponsorName ? ` · ${w.prize.sponsorName}` : ''}
                </dd>
              </div>
              <div>
                <dt>Claim</dt>
                <dd>
                  {w.claimStatus ? <StatusBadge kind="ClaimStatus" value={w.claimStatus} /> : 'Not opened'}
                </dd>
              </div>
              <div>
                <dt>External verification</dt>
                <dd>
                  {w.verification ? <StatusBadge kind="VerificationDecision" value={w.verification} /> : '—'}
                </dd>
              </div>
              <div>
                <dt>Fulfillment</dt>
                <dd>
                  {w.fulfillmentStatus ? (
                    <StatusBadge kind="FulfillmentStatus" value={w.fulfillmentStatus} />
                  ) : (
                    '—'
                  )}
                </dd>
              </div>
              <div>
                <dt>Chosen variant</dt>
                <dd>{data.claimSummary.selectedVariant ?? '—'}</dd>
              </div>
              <div>
                <dt>Delivery preference</dt>
                <dd>{data.claimSummary.deliveryPreference ?? '—'}</dd>
              </div>
              <div>
                <dt>Legal name</dt>
                <dd>{data.claimSummary.legalNameMasked ?? '—'}</dd>
              </div>
              <div>
                <dt>Claim deadline</dt>
                <dd>{intl.formatDateTime(w.claimDeadline)}</dd>
              </div>
            </dl>
          </section>
          <section className="wdc-panel">
            <h2>Contact and messaging</h2>
            <dl className="wdc-facts">
              <div>
                <dt>Display name</dt>
                <dd>{w.displayName}</dd>
              </div>
              <div>
                <dt>Platform</dt>
                <dd>{w.platform ? humanizeStatus(w.platform) : '—'}</dd>
              </div>
              <div>
                <dt>Handle</dt>
                <dd>
                  {w.channelId ? (
                    <MaskedCell
                      masked={w.handleMasked}
                      what="contact handle"
                      subject={{
                        kind: 'PARTICIPANT_CONTACT',
                        participantId: w.participantId,
                        channelId: w.channelId,
                      }}
                    />
                  ) : w.claimId ? (
                    <MaskedCell
                      masked={w.handleMasked}
                      what="contact handle"
                      subject={{ kind: 'CLAIM_IDENTITY', claimId: w.claimId }}
                    />
                  ) : (
                    <code>{w.handleMasked ?? '—'}</code>
                  )}
                </dd>
              </div>
              <div>
                <dt>Messaging</dt>
                <dd>
                  <StatusBadge kind="WinnerContactStatus" value={w.contactStatus} />
                  {w.buttonTappedAt ? (
                    <div className="wd-muted">
                      Button tapped {intl.formatDateTime(w.buttonTappedAt)} (not a delivered message)
                    </div>
                  ) : null}
                  {w.messagedAt ? (
                    <div className="wd-muted">Marked messaged {intl.formatDateTime(w.messagedAt)}</div>
                  ) : null}
                  {w.followedUpAt ? (
                    <div className="wd-muted">Followed up {intl.formatDateTime(w.followedUpAt)}</div>
                  ) : null}
                </dd>
              </div>
              <div>
                <dt>Assigned</dt>
                <dd>
                  {w.assignedTo?.name ?? 'Unassigned'}{' '}
                  <Can permission="winners.assign">
                    {session && w.assignedTo?.staffId !== session.staffId ? (
                      <Button variant="quiet" onClick={() => update.mutate({ assignTo: session.staffId })}>
                        Assign to me
                      </Button>
                    ) : w.assignedTo ? (
                      <Button variant="quiet" onClick={() => update.mutate({ assignTo: null })}>
                        Unassign
                      </Button>
                    ) : null}
                  </Can>
                </dd>
              </div>
              <div>
                <dt>Reminder</dt>
                <dd>
                  {w.reminderAt
                    ? `${intl.formatDateTime(w.reminderAt)}${w.reminderNote ? ` — ${w.reminderNote}` : ''}`
                    : 'None'}
                  {w.reminderAt ? (
                    <div className="wd-muted">Also shown on the winner's status page.</div>
                  ) : null}
                </dd>
              </div>
            </dl>
            <Can permission="winners.mark_messaged">
              <div className="wd-btn-group">
                <Button
                  variant="primary"
                  disabled={!canMark('MESSAGED')}
                  disabledReason={canMark('MESSAGED') ? undefined : 'Already marked'}
                  onClick={() =>
                    update.mutate({
                      contactStatus: 'MESSAGED',
                      ...(w.platform ? { platform: w.platform } : {}),
                    })
                  }
                  loading={update.isPending}
                >
                  Mark messaged
                </Button>
                <Button
                  variant="secondary"
                  disabled={!canMark('FOLLOWED_UP')}
                  disabledReason={canMark('FOLLOWED_UP') ? undefined : 'Mark messaged first'}
                  onClick={() =>
                    update.mutate({ contactStatus: 'FOLLOWED_UP', ...(outcome ? { outcome } : {}) })
                  }
                  loading={update.isPending}
                >
                  Mark followed up
                </Button>
              </div>
              {canMark('FOLLOWED_UP') ? (
                <Field
                  label="Follow-up outcome"
                  hint="Recorded with the follow-up (e.g. pickup agreed for Saturday)."
                >
                  <Input value={outcome} onChange={(e) => setOutcome(e.target.value)} />
                </Field>
              ) : null}
            </Can>
          </section>
          <Can permission="winners.assign">
            <section className="wdc-panel">
              <h2>Notes and reminders</h2>
              <form onSubmit={submitNote} className="wd-stack">
                <Field label="Follow-up note">
                  <Input value={note} onChange={(e) => setNote(e.target.value)} data-testid="winner-note" />
                </Field>
                <Field
                  label="Remind me at"
                  hint="The reminder is e-mailed to the assigned admin and shown on the winner's status page."
                >
                  <Input
                    type="datetime-local"
                    value={reminderAt}
                    onChange={(e) => setReminderAt(e.target.value)}
                  />
                </Field>
                <Button
                  type="submit"
                  variant="secondary"
                  loading={update.isPending}
                  disabled={!note.trim() && !reminderAt}
                >
                  Save
                </Button>
              </form>
              <Timeline
                events={data.notes.map((n) => ({ id: n.noteId, at: n.at, title: n.text, actor: n.staffId }))}
                emptyText="No follow-up notes yet."
              />
            </section>
            <section className="wdc-panel">
              <h2>Messaged from a different handle?</h2>
              <p className="wd-muted">
                Verify by win ID and signed link first, then record the new handle on the profile (Appendix
                A).
              </p>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (handle.trim()) recordHandle.mutate();
                }}
                className="wd-stack"
              >
                <Field label="Platform">
                  <Select
                    options={SocialPlatform.options.map((p) => ({ value: p, label: humanizeStatus(p) }))}
                    value={handlePlatform}
                    onChange={(e) => setHandlePlatform(e.target.value as SocialPlatform)}
                  />
                </Field>
                <Field label="Handle seen in the conversation">
                  <Input
                    value={handle}
                    onChange={(e) => setHandle(e.target.value)}
                    placeholder="@handle"
                    data-testid="winner-handle"
                  />
                </Field>
                <Button
                  type="submit"
                  variant="secondary"
                  loading={recordHandle.isPending}
                  disabled={!handle.trim()}
                >
                  Record handle
                </Button>
              </form>
            </section>
          </Can>
          <section className="wdc-panel wdc-span-all">
            <h2>Claim timeline</h2>
            <Timeline
              events={data.claimSummary.timeline.map((t, i) => ({
                id: `${t.status}-${i}`,
                at: t.at,
                title: humanizeStatus(t.status),
              }))}
              emptyText="No claim activity yet."
            />
          </section>
        </div>
      ) : null}
    </>
  );
}
